import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  type Annotation,
  type Point,
  annotationBounds,
  drawAnnotations,
  hitTest,
  isResizable,
  moveAnnotation,
  newAnnotationId,
  normalizeBox,
  resizeAnnotation,
  TEXT_FONT,
  TEXT_LINE_HEIGHT,
} from "@/lib/docAnnotate/annotations";
import type { PDFDocumentProxy } from "@/lib/docAnnotate/pdfjs";

export type Tool =
  | "pan"
  | "select"
  | "pen"
  | "text"
  | "rect"
  | "rectFilled"
  | "ellipse"
  | "ellipseFilled"
  | "arrow"
  | "signature";

export type DocSource =
  | { kind: "pdf"; pdf: PDFDocumentProxy }
  | { kind: "image"; img: HTMLImageElement };

export interface ToolSettings {
  tool: Tool;
  color: string;
  width: number;
  textSize: number;
}

export interface TextDraft {
  page: number;
  x: number;
  y: number;
  value: string;
  size: number;
  color: string;
}

const MAX_CANVAS_PIXELS = 8_000_000;
const HANDLE_CSS = 14;

interface AnnotatorPageProps {
  index: number;
  source: DocSource;
  pageWidth: number;
  pageHeight: number;
  cssWidth: number;
  annotations: Annotation[];
  selectedId: string | null;
  settings: ToolSettings;
  onAdd: (annotation: Annotation) => void;
  onChange: (annotation: Annotation, final: boolean) => void;
  onSelect: (id: string | null) => void;
  onTextRequest: (page: number, point: Point) => void;
  onSignatureRequest: (page: number, point: Point) => void;
  textDraft?: TextDraft | null;
  textPlaceholder?: string;
  onTextDraftChange?: (value: string) => void;
  onTextCommit?: () => void;
  onTextCancel?: () => void;
}

type Gesture =
  | { kind: "draw"; draft: Annotation }
  | { kind: "move"; origin: Annotation; start: Point; current: Annotation }
  | { kind: "resize"; origin: Annotation; start: Point; current: Annotation };

function AnnotatorPage({
  index,
  source,
  pageWidth,
  pageHeight,
  cssWidth,
  annotations,
  selectedId,
  settings,
  onAdd,
  onChange,
  onSelect,
  onTextRequest,
  onSignatureRequest,
  textDraft,
  textPlaceholder,
  onTextDraftChange,
  onTextCommit,
  onTextCancel,
}: AnnotatorPageProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const baseRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const [gesture, setGesture] = useState<Gesture | null>(null);
  const [visible, setVisible] = useState(false);
  const [imageTick, setImageTick] = useState(0);

  const cssHeight = (cssWidth / pageWidth) * pageHeight;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const pixelRatio = Math.min(dpr, Math.sqrt(MAX_CANVAS_PIXELS / (cssWidth * cssHeight)));
  const pixelWidth = Math.max(1, Math.floor(cssWidth * pixelRatio));
  const pixelHeight = Math.max(1, Math.floor(cssHeight * pixelRatio));
  const unitsPerCss = pageWidth / cssWidth;

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { rootMargin: "800px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = baseRef.current;
    if (!canvas) return;
    if (!visible) {
      canvas.width = 0;
      canvas.height = 0;
      return;
    }
    canvas.width = pixelWidth;
    canvas.height = pixelHeight;
    const ctx = canvas.getContext("2d")!;
    if (source.kind === "image") {
      ctx.drawImage(source.img, 0, 0, pixelWidth, pixelHeight);
      canvas.dataset.rendered = "true";
      return;
    }
    let cancelled = false;
    let task: { cancel: () => void; promise: Promise<void> } | null = null;
    source.pdf.getPage(index + 1).then((page) => {
      if (cancelled) return;
      const viewport = page.getViewport({ scale: pixelWidth / pageWidth });
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, pixelWidth, pixelHeight);
      task = page.render({ canvasContext: ctx, viewport });
      task.promise
        .then(() => {
          if (!cancelled) canvas.dataset.rendered = "true";
        })
        .catch(() => undefined);
    });
    return () => {
      cancelled = true;
      delete canvas.dataset.rendered;
      task?.cancel();
    };
  }, [visible, source, index, pixelWidth, pixelHeight, pageWidth]);

  const live = gesture
    ? gesture.kind === "draw"
      ? [...annotations, gesture.draft]
      : annotations.map((a) => (a.id === gesture.origin.id ? gesture.current : a))
    : annotations;

  useLayoutEffect(() => {
    const canvas = overlayRef.current;
    if (!canvas) return;
    if (!visible) {
      canvas.width = 0;
      canvas.height = 0;
      return;
    }
    canvas.width = pixelWidth;
    canvas.height = pixelHeight;
    const ctx = canvas.getContext("2d")!;
    const k = pixelWidth / pageWidth;
    ctx.setTransform(k, 0, 0, k, 0, 0);
    drawAnnotations(ctx, live, () => setImageTick((n) => n + 1));
    const selected = live.find((a) => a.id === selectedId);
    if (selected) {
      const b = annotationBounds(selected);
      const pad = 4 * unitsPerCss;
      ctx.save();
      ctx.lineWidth = 1.5 * unitsPerCss;
      ctx.strokeStyle = "#1677ff";
      ctx.setLineDash([5 * unitsPerCss, 4 * unitsPerCss]);
      ctx.strokeRect(b.x - pad, b.y - pad, b.w + pad * 2, b.h + pad * 2);
      if (isResizable(selected)) {
        const hs = HANDLE_CSS * unitsPerCss;
        ctx.setLineDash([]);
        ctx.fillStyle = "#fff";
        ctx.fillRect(b.x + b.w + pad - hs / 2, b.y + b.h + pad - hs / 2, hs, hs);
        ctx.strokeRect(b.x + b.w + pad - hs / 2, b.y + b.h + pad - hs / 2, hs, hs);
      }
      ctx.restore();
    }
  }, [visible, live, selectedId, pixelWidth, pixelHeight, pageWidth, unitsPerCss, imageTick]);

  const toPoint = useCallback(
    (e: React.PointerEvent): Point => {
      const rect = overlayRef.current!.getBoundingClientRect();
      return {
        x: ((e.clientX - rect.left) / rect.width) * pageWidth,
        y: ((e.clientY - rect.top) / rect.height) * pageHeight,
      };
    },
    [pageWidth, pageHeight],
  );

  const updateGesture = (next: Gesture | null) => {
    gestureRef.current = next;
    setGesture(next);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const { tool, color, width } = settings;
    if (tool === "pan" || e.button > 0) return;
    const p = toPoint(e);
    e.preventDefault();

    if (tool === "text") {
      onTextRequest(index, p);
      return;
    }
    if (tool === "signature") {
      onSignatureRequest(index, p);
      return;
    }

    e.currentTarget.setPointerCapture?.(e.pointerId);

    if (tool === "select") {
      const selected = annotations.find((a) => a.id === selectedId);
      if (selected && isResizable(selected)) {
        const b = annotationBounds(selected);
        const pad = 4 * unitsPerCss;
        const hs = HANDLE_CSS * unitsPerCss;
        if (Math.abs(p.x - (b.x + b.w + pad)) <= hs && Math.abs(p.y - (b.y + b.h + pad)) <= hs) {
          updateGesture({ kind: "resize", origin: selected, start: p, current: selected });
          return;
        }
      }
      const tolerance = 6 * unitsPerCss;
      const hit = [...annotations].reverse().find((a) => hitTest(a, p, tolerance));
      onSelect(hit?.id ?? null);
      if (hit) updateGesture({ kind: "move", origin: hit, start: p, current: hit });
      return;
    }

    const base = { id: newAnnotationId(), page: index, color };
    let draft: Annotation;
    if (tool === "pen") draft = { ...base, type: "pen", points: [p], width };
    else if (tool === "arrow") draft = { ...base, type: "arrow", x1: p.x, y1: p.y, x2: p.x, y2: p.y, width };
    else
      draft = {
        ...base,
        type: tool.startsWith("rect") ? "rect" : "ellipse",
        x: p.x,
        y: p.y,
        w: 0,
        h: 0,
        filled: tool.endsWith("Filled"),
        width,
      };
    updateGesture({ kind: "draw", draft });
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const g = gestureRef.current;
    if (!g) return;
    const p = toPoint(e);
    if (g.kind === "draw") {
      const d = g.draft;
      if (d.type === "pen") {
        const last = d.points[d.points.length - 1];
        if (Math.hypot(p.x - last.x, p.y - last.y) < 0.75 * unitsPerCss) return;
        updateGesture({ kind: "draw", draft: { ...d, points: [...d.points, p] } });
      } else if (d.type === "arrow") {
        updateGesture({ kind: "draw", draft: { ...d, x2: p.x, y2: p.y } });
      } else if (d.type === "rect" || d.type === "ellipse") {
        updateGesture({ kind: "draw", draft: { ...d, w: p.x - d.x, h: p.y - d.y } });
      }
      return;
    }
    if (g.kind === "move") {
      const current = moveAnnotation(g.origin, p.x - g.start.x, p.y - g.start.y);
      updateGesture({ ...g, current });
      return;
    }
    if (isResizable(g.origin)) {
      const current = resizeAnnotation(
        g.origin,
        g.origin.w + p.x - g.start.x,
        g.origin.h + p.y - g.start.y,
      );
      updateGesture({ ...g, current });
    }
  };

  const handlePointerUp = () => {
    const g = gestureRef.current;
    if (!g) return;
    updateGesture(null);
    if (g.kind === "draw") {
      const d = g.draft;
      const min = 3 * unitsPerCss;
      if (d.type === "pen") onAdd(d);
      else if (d.type === "arrow") {
        if (Math.hypot(d.x2 - d.x1, d.y2 - d.y1) >= min) onAdd(d);
      } else if (d.type === "rect" || d.type === "ellipse") {
        const box = normalizeBox(d);
        if (box.w >= min && box.h >= min) onAdd(box);
      }
      return;
    }
    if (g.current !== g.origin) onChange(g.current, true);
  };

  const interactive = settings.tool !== "pan";

  return (
    <div
      ref={wrapperRef}
      data-page-index={index}
      data-testid={`annotator-page-${index}`}
      style={{
        position: "relative",
        width: cssWidth,
        height: cssHeight,
        background: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
        margin: "0 auto 16px",
      }}
    >
      <canvas
        ref={baseRef}
        data-testid={`annotator-base-${index}`}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      />
      <canvas
        ref={overlayRef}
        data-testid={`annotator-overlay-${index}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          touchAction: interactive ? "none" : "auto",
          cursor: interactive ? (settings.tool === "select" ? "default" : "crosshair") : "grab",
        }}
      />
      {textDraft && (
        <textarea
          autoFocus
          data-testid="annotator-text-input"
          value={textDraft.value}
          placeholder={textPlaceholder}
          rows={Math.max(1, textDraft.value.split("\n").length)}
          onChange={(e) => onTextDraftChange?.(e.target.value)}
          onBlur={() => onTextCommit?.()}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              onTextCancel?.();
            } else if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              onTextCommit?.();
            }
          }}
          style={{
            position: "absolute",
            left: textDraft.x / unitsPerCss,
            top: textDraft.y / unitsPerCss,
            minWidth: 120,
            maxWidth: Math.max(120, cssWidth - textDraft.x / unitsPerCss),
            font: `${textDraft.size / unitsPerCss}px ${TEXT_FONT}`,
            lineHeight: TEXT_LINE_HEIGHT,
            color: textDraft.color,
            background: "rgba(255,255,255,0.85)",
            border: "1px dashed #1677ff",
            outline: "none",
            padding: 0,
            margin: 0,
            resize: "none",
            overflow: "hidden",
            zIndex: 2,
          }}
        />
      )}
    </div>
  );
}

export default AnnotatorPage;
